import { readFrontendSiteLinks } from '@/frontendConfig';
import type { MarketingPageState } from '@/marketing/context';
import { walletLanguageUrl, walletPagePath, type Locale } from '@/marketing/i18n/locale';

const SITE_LINKS = readFrontendSiteLinks(import.meta.env);

const DOCS_PREFIX = '/docs';

function stripTrailingSlash(path: string): string {
  if (path.length <= 1) return path;
  return path.endsWith('/') ? path.slice(0, -1) : path;
}

export function getDocsOrigin(): string {
  return stripTrailingSlash(SITE_LINKS.docsOrigin);
}

export function isHttpUrl(value: string): boolean {
  return /^https?:\/\//.test(value);
}

export function getWalletLanguageHref(page: MarketingPageState['page'], language: Locale): string {
  return walletLanguageUrl(new URL(window.location.href), SITE_LINKS.walletPath, page, language);
}

function toSiteAbsolutePath(state: MarketingPageState, pathOrHash: string): string {
  if (!pathOrHash) return '/';
  if (pathOrHash.startsWith('#'))
    return `${walletPagePath(SITE_LINKS.walletPath, state.page, state.locale)}${pathOrHash}`;
  return pathOrHash.startsWith('/') ? pathOrHash : `/${pathOrHash}`;
}

export function maybeDocsHref(locale: Locale, target: string): string | null {
  if (!target.startsWith(DOCS_PREFIX)) return null;
  const docsOrigin = getDocsOrigin();
  const suffix = target === DOCS_PREFIX ? '/' : target.slice(DOCS_PREFIX.length);
  if (locale === 'ja' && normalizePathname(target) === DOCS_PREFIX) {
    return `${docsOrigin}/ja/${suffix.replace(/^\//, '')}`;
  }
  return `${docsOrigin}${suffix.startsWith('/') ? suffix : `/${suffix}`}`;
}

export function resolveHref(state: MarketingPageState, target: string): string {
  const { locale } = state;
  if (isHttpUrl(target)) return target;
  if (target.startsWith('#')) return toSiteAbsolutePath(state, target);

  if (normalizePathname(target) === '/wallet') {
    const url = new URL(target, 'https://wallet.seams.sh');
    return `${walletPagePath(SITE_LINKS.walletPath, 'home', locale)}${url.search}${url.hash}`;
  }

  const docsHref = maybeDocsHref(locale, target);
  if (docsHref) return docsHref;

  const pathname = normalizePathname(target);
  switch (pathname) {
    case '/pricing':
    case '/features': {
      const url = new URL(target, 'https://wallet.seams.sh');
      const page = pathname === '/pricing' ? 'pricing' : 'features';
      return `${walletPagePath(SITE_LINKS.walletPath, page, locale)}${url.search}${url.hash}`;
    }
    case '/':
    case '/company':
    case '/ecommerce': {
      const base = SITE_LINKS.companyOrigin === '/' ? '' : SITE_LINKS.companyOrigin;
      if (locale === 'ja' && pathname === '/company') {
        const url = new URL(target, 'https://wallet.seams.sh');
        return `${base}/ja${url.pathname}${url.search}${url.hash}`;
      }
      return `${base}${toSiteAbsolutePath(state, target)}`;
    }
    default:
      return toSiteAbsolutePath(state, target);
  }
}

export function normalizePathname(pathname: string): string {
  if (!pathname) return '/';
  const clean = pathname.split('?')[0].split('#')[0];
  const normalized = clean.startsWith('/') ? clean : `/${clean}`;
  return stripTrailingSlash(normalized) || '/';
}

function scrollToSiteAnchor(): void {
  const id = window.location.hash.slice(1);
  if (id) document.getElementById(id)?.scrollIntoView();
}

function unsubscribeFromSiteNavigation(listener: () => void): void {
  window.removeEventListener('popstate', listener);
  window.removeEventListener('hashchange', listener);
}

export function subscribeToSiteNavigation(listener: () => void): () => void {
  window.addEventListener('popstate', listener);
  window.addEventListener('hashchange', listener);
  return unsubscribeFromSiteNavigation.bind(null, listener);
}

export function startSiteAnchorScroll(): () => void {
  scrollToSiteAnchor();
  return subscribeToSiteNavigation(scrollToSiteAnchor);
}
